import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Select } from '../ui/Select';
import { Badge } from '../ui/Badge';
// Nav items per platform
const platformNavItems: Record<string, {
  icon: string;
  label: string;
  path: string;
  badge?: number;
}[]> = {
  bandi: [{
    icon: 'tabler:smart-home',
    label: 'Home',
    path: '/dashboard'
  }, {
    icon: 'tabler:gavel',
    label: 'Gestione Bandi',
    path: '/bandi'
  }, {
    icon: 'mdi:account-check-outline',
    label: 'Gestione Valutatori',
    path: '/valutatori'
  }, {
    icon: 'mdi:account-multiple-outline',
    label: 'Gestione Utenti',
    path: '/gestione-utenti'
  }, {
    icon: 'mdi:file-edit-outline',
    label: 'Modifiche a Soggetti',
    path: '/modifiche',
    badge: 24
  }, {
    icon: 'tabler:building-skyscraper',
    label: 'Soggetti Proponenti',
    path: '/soggetti'
  }],
  email: [{
    icon: 'tabler:mail',
    label: 'Campagne',
    path: '/email/campagne'
  }, {
    icon: 'tabler:template',
    label: 'Templates',
    path: '/email/templates'
  }],
  ticket: [{
    icon: 'tabler:list',
    label: 'Tutti i Ticket',
    path: '/ticket/tutti'
  }, {
    icon: 'tabler:mail-forward',
    label: 'Assegnati a te',
    path: '/ticket/assegnati-a-te'
  }, {
    icon: 'tabler:chart-pie',
    label: 'Analisi Ticket',
    path: '/ticket/analisi'
  }],
  faq: [{
    icon: 'tabler:help-circle',
    label: 'Gestione FAQ',
    path: '/faq'
  }],
  diario: [{
    icon: 'tabler:notebook',
    label: 'Diario Per Chi Crea',
    path: '/diario'
  }]
};
const piattaformaOptions = [{
  value: 'bandi',
  label: 'Bandi'
}, {
  value: 'email',
  label: 'Email'
}, {
  value: 'ticket',
  label: 'Gestione Ticket'
}, {
  value: 'faq',
  label: 'FAQ'
}, {
  value: 'diario',
  label: 'Diario'
}];
const candidatureSubmenu = [{
  label: 'Tutte',
  path: '/candidature',
  id: 'tutte'
}, {
  label: 'Da assegnare agli admin',
  path: '/candidature',
  id: 'da-assegnare-admin'
}, {
  label: 'In verifica di ammissibilità',
  path: '/candidature',
  id: 'verifica-ammissibilita'
}, {
  label: 'Assegnate a te',
  path: '/candidature',
  id: 'assegnate-a-te'
}, {
  label: 'Non ammesse',
  path: '/candidature',
  id: 'non-ammesse'
}, {
  label: 'Da assegnare ai valutatori',
  path: '/candidature',
  id: 'da-assegnare-valutatori'
}, {
  label: 'In valutazione',
  path: '/candidature',
  id: 'in-valutazione'
}, {
  label: 'Valutate',
  path: '/candidature',
  id: 'valutate'
}, {
  label: 'Vincitori',
  path: '/candidature',
  id: 'vincitori'
}, {
  label: 'Doc. da Controllare',
  path: '/candidature',
  id: 'doc-da-controllare'
}, {
  label: 'Variazioni',
  path: '/candidature',
  id: 'variazioni'
}, {
  label: 'In Rendicontazione',
  path: '/candidature',
  id: 'in-rendicontazione'
}, {
  label: 'Rendicontate',
  path: '/candidature',
  id: 'rendicontate'
}, {
  label: 'Revoche',
  path: '/candidature',
  id: 'revoche'
}];
interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  aiModeActive?: boolean;
  onAiModeToggle?: () => void;
}
export function Sidebar({
  isOpen = false,
  onClose,
  aiModeActive = false,
  onAiModeToggle
}: SidebarProps) {
  const location = useLocation();
  const [piattaforma, setPiattaforma] = useState('bandi');
  const [piattaformaDropdownOpen, setPiattaformaDropdownOpen] = useState(false);
  const [anno, setAnno] = useState('2025');
  const [annoDropdownOpen, setAnnoDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const annoDropdownRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setPiattaformaDropdownOpen(false);
      }
      if (annoDropdownRef.current && !annoDropdownRef.current.contains(e.target as Node)) {
        setAnnoDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleNavClick = () => {
    if (onClose) onClose();
  };
  const currentNavItems = platformNavItems[piattaforma] || platformNavItems.bandi;
  const isBandi = piattaforma === 'bandi';
  const showEditionControls = piattaforma !== 'ticket' && piattaforma !== 'faq';
  const annoOptions = [{
    value: '2025',
    label: '2025'
  }, {
    value: '2024',
    label: '2024'
  }, {
    value: '2023',
    label: '2023'
  }];
  return <>
      {/* Mobile overlay backdrop */}
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={onClose} aria-hidden="true" data-id="element-211" />}

      <aside className={`w-[260px] text-white flex flex-col h-screen fixed left-0 top-0 z-50 overflow-y-auto border-r border-gray-800 bg-[#2A3A54] transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`} data-id="element-212">
        {/* Logo Area */}
        <div className="p-5 pb-[10px]" data-id="element-213">
          <div className="mb-5 flex items-center justify-between" data-id="element-214">
            <img src="https://dev.bandiperchicrea.it/images/logo.svg" alt="Per Chi Crea" className="h-12 w-auto" data-id="element-215" />
            <div className="flex items-center gap-2" data-id="element-216">
              {/* Anno Select — inline with logo, blue background */}
              {showEditionControls && <div className="relative" ref={annoDropdownRef} data-id="element-217">
                  <button onClick={() => setAnnoDropdownOpen(!annoDropdownOpen)} className="h-10 px-3 flex items-center gap-2 text-sm font-medium text-white bg-[#2A97DF] hover:bg-[#2488c8] transition-colors rounded-full" data-id="element-218">
                    <span data-id="element-219">{anno}</span>
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" className="flex-shrink-0" data-id="element-220">
                      <polygon points="0,0 10,0 5,6" fill="white" data-id="element-221" />
                    </svg>
                  </button>

                  {/* Anno Dropdown */}
                  {annoDropdownOpen && <div className="absolute right-0 top-full mt-1 w-[120px] bg-white shadow-xl z-[60] border border-gray-200 rounded-2xl overflow-hidden" data-id="element-222">
                      {annoOptions.map(opt => <button key={opt.value} onClick={() => {
                  setAnno(opt.value);
                  setAnnoDropdownOpen(false);
                }} className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${anno === opt.value ? 'bg-[#2A97DF] text-white' : 'text-gray-800 hover:bg-gray-100'}`} data-id="element-223">
                          {opt.label}
                        </button>)}
                    </div>}
                </div>}
              <button className="md:hidden text-gray-400 hover:text-white p-1" onClick={onClose} aria-label="Chiudi menu" data-id="element-224">
                <Icon icon="tabler:x" fontSize={20} data-id="element-225" />
              </button>
            </div>
          </div>

          {/* Piattaforma Select — full width */}
          <div className="mb-6" ref={dropdownRef} data-id="element-226">
            <div className="flex flex-col gap-1" data-id="element-227">
              <label className="text-xs text-gray-400 font-medium mb-[10px]" data-id="element-228">
                Seleziona una piattaforma
              </label>
              <div className="relative" data-id="element-229">
                <button onClick={() => setPiattaformaDropdownOpen(!piattaformaDropdownOpen)} className="w-full h-10 px-4 flex items-center justify-between text-sm text-white bg-transparent border border-gray-600 hover:border-gray-400 transition-colors rounded-full" data-id="element-230">
                  <span data-id="element-231">
                    {piattaformaOptions.find(o => o.value === piattaforma)?.label}
                  </span>
                  <svg width="12" height="7" viewBox="0 0 12 7" fill="none" className="flex-shrink-0 ml-1" data-id="element-232">
                    <polygon points="0,0 12,0 6,7" fill="white" data-id="element-233" />
                  </svg>
                </button>

                {/* Dropdown panel */}
                {piattaformaDropdownOpen && <div className="absolute left-0 top-full mt-1 w-full bg-[#1a2d44] shadow-xl z-[60] border border-white/15 rounded-2xl overflow-hidden" data-id="element-234">
                    {piattaformaOptions.map(opt => <button key={opt.value} onClick={() => {
                  setPiattaforma(opt.value);
                  setPiattaformaDropdownOpen(false);
                }} className={`w-full text-left px-4 py-3 text-sm transition-colors ${piattaforma === opt.value ? 'bg-[#2A97DF] text-white' : 'text-gray-300 hover:bg-white/10'}`} data-id="element-235">
                        {opt.label}
                      </button>)}
                  </div>}
              </div>
            </div>
          </div>

          <div className="h-px bg-gray-700 w-full mb-2" data-id="element-236"></div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 pb-6" data-id="element-237">
          <ul className="space-y-1" data-id="element-238">
            {isBandi /* Bandi platform: special layout with collapsible Candidature */ ? <>
                {/* Home */}
                <li data-id="element-239">
                  <Link to="/dashboard" onClick={handleNavClick} className="flex items-center justify-between px-3 py-2.5 rounded-md transition-all duration-200 group text-gray-400 hover:text-gray-200 hover:bg-white/5" data-id="element-240">
                    <div className="flex items-center gap-3" data-id="element-241">
                      <div className="text-gray-400 group-hover:text-gray-200" data-id="element-242">
                        <Icon icon="tabler:smart-home" fontSize={22} data-id="element-243" />
                      </div>
                      <span className="text-sm tracking-wide font-[500]" data-id="element-244">
                        Home
                      </span>
                    </div>
                  </Link>
                </li>

                {/* Gestione Bandi */}
                <li data-id="element-245">
                  <Link to="/bandi" onClick={handleNavClick} className="flex items-center justify-between px-3 py-2.5 rounded-md transition-all duration-200 group text-gray-400 hover:text-gray-200 hover:bg-white/5" data-id="element-246">
                    <div className="flex items-center gap-3" data-id="element-247">
                      <div className="text-gray-400 group-hover:text-gray-200" data-id="element-248">
                        <Icon icon="tabler:gavel" fontSize={22} data-id="element-249" />
                      </div>
                      <span className="text-sm font-medium tracking-wide" data-id="element-250">
                        Gestione Bandi
                      </span>
                    </div>
                  </Link>
                </li>

                {/* Gestione Candidature — direct link */}
                <li data-id="element-251">
                  <Link to="/candidature" onClick={handleNavClick} className="flex items-center justify-between px-3 py-2.5 rounded-md transition-all duration-200 group text-gray-400 hover:text-gray-200 hover:bg-white/5" data-id="element-252">
                    <div className="flex items-center gap-3" data-id="element-253">
                      <div className="text-gray-400 group-hover:text-gray-200" data-id="element-254">
                        <Icon icon="mdi:account-cog-outline" fontSize={22} data-id="element-255" />
                      </div>
                      <span className="text-sm font-medium tracking-wide" data-id="element-256">
                        Gestione Candidature
                      </span>
                    </div>
                  </Link>
                </li>

                {/* Remaining bandi nav items */}
                {[{
              icon: 'mdi:account-check-outline',
              label: 'Gestione Valutatori',
              path: '/valutatori'
            }, {
              icon: 'mdi:account-multiple-outline',
              label: 'Gestione Utenti',
              path: '/gestione-utenti'
            }, {
              icon: 'mdi:file-edit-outline',
              label: 'Modifiche a Soggetti',
              path: '/modifiche',
              badge: 24
            }, {
              icon: 'tabler:building-skyscraper',
              label: 'Soggetti Proponenti',
              path: '/soggetti'
            }].map(item => <li key={item.path} data-id="element-257">
                    <Link to={item.path} onClick={handleNavClick} className="flex items-center justify-between px-3 py-2.5 rounded-md transition-all duration-200 group text-gray-400 hover:text-gray-200 hover:bg-white/5" data-id="element-258">
                      <div className="flex items-center gap-3" data-id="element-259">
                        <div className="text-gray-400 group-hover:text-gray-200" data-id="element-260">
                          <Icon icon={item.icon} fontSize={22} data-id="element-261" />
                        </div>
                        <span className="text-sm font-medium tracking-wide" data-id="element-262">
                          {item.label}
                        </span>
                      </div>
                      {item.badge && <span className="bg-red-500 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full min-w-[20px] text-center" data-id="element-263">
                          {item.badge}
                        </span>}
                    </Link>
                  </li>)}
              </> /* Other platforms: flat nav list */ : currentNavItems.map(item => <li key={item.path} data-id="element-264">
                  <Link to={item.path} onClick={handleNavClick} className="flex items-center justify-between px-3 py-2.5 rounded-md transition-all duration-200 group text-gray-400 hover:text-gray-200 hover:bg-white/5" data-id="element-265">
                    <div className="flex items-center gap-3" data-id="element-266">
                      <div className="text-gray-400 group-hover:text-gray-200" data-id="element-267">
                        <Icon icon={item.icon} fontSize={22} data-id="element-268" />
                      </div>
                      <span className="text-sm font-medium tracking-wide" data-id="element-269">
                        {item.label}
                      </span>
                    </div>
                    {item.badge && <span className="bg-red-500 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full min-w-[20px] text-center" data-id="element-270">
                        {item.badge}
                      </span>}
                  </Link>
                </li>)}
          </ul>
        </nav>

        {/* AI Mode — bottom of sidebar */}
        <div className="px-5 pb-5 pt-2 flex-shrink-0" data-id="element-271">
          <div className="h-px bg-gray-700 w-full mb-4" data-id="element-272"></div>
          <button onClick={onAiModeToggle} className="w-full flex items-center justify-between px-3 py-2.5 rounded-md transition-all duration-200 group hover:bg-white/5" data-id="element-273">
            <span className={`${`text-sm tracking-wide transition-colors duration-200 ${aiModeActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-200'}`} text-[16px] font-[400] font-['RobotoCondensed_VariableFont_Wght',_sans-serif] text-[#FFFFFF]`} data-id="element-274">
              AI Mode
            </span>

            <div className={`relative w-[34px] h-[18px] rounded-full transition-colors duration-200 flex-shrink-0 ${aiModeActive ? 'bg-[#2A97DF]' : 'bg-[#2a3a50] group-hover:bg-[#344a64]'}`} data-id="element-275">
              <div className={`absolute top-[3px] left-[3px] w-3 h-3 rounded-full transition-all duration-200 ${aiModeActive ? 'translate-x-4 bg-white' : 'translate-x-0 bg-gray-500'}`} data-id="element-276" />
            </div>
          </button>
        </div>
      </aside>
    </>;
}